// Admin → Referee Codes
//
// CRUD for the small list of role chips (default: CC / R / T) that prefix
// each referee slot in the Game Planner. Admins can rename the labels,
// add their own (e.g. "Mentor"), reorder, or archive.
//
// Each GameAssignmentSlots row (referee role only) carries an optional
// reference to one of these codes. New referee slots default to the
// code at displayOrder = slotOrder so the historical UX (slot 1 = CC,
// slot 2 = R, slot 3 = T) is preserved without any frontend change.
// Game Planner cycles a slot through the codes by clicking the chip;
// My Portal and Roster View display the code read-only.

import React, { useEffect, useState, useCallback } from 'react';
import { auditFetch } from '../auditFetch';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://referee-roster-api-v2-hqghffesdfegd2dq.australiaeast-01.azurewebsites.net/api';

// The same screen also runs Admin → Coach Codes (kind="coach"): chips before
// each coach on a Coach Planner tile, cycled by clicking.
const KINDS = {
  referee: {
    path: 'referee-codes',
    title: 'Referee Codes',
    noun: 'referee',
    intro: 'Role chips that appear before each referee slot on the Game Planner. Admins can rename, add, reorder, or archive. '
      + 'New referee slots default to the code matching their slot order (slot 1 → first code, slot 2 → second, etc.). '
      + 'On the Game Planner, click a chip to cycle the slot through the configured codes. My Portal and Roster View show the code read-only.',
    archiveNote: 'Existing referee slots that reference it will keep their assigned code; you can change them by cycling on the Game Planner.',
  },
  coach: {
    path: 'coach-codes',
    title: 'Coach Codes',
    noun: 'coach',
    intro: 'Role chips that appear before each coach on a Coach Planner session. Admins can rename, add, reorder, or archive. '
      + 'On the Coach Planner, click the chip next to a coach to cycle through these codes in order.',
    archiveNote: 'Coaches already assigned with it keep it; you can change them by cycling on the Coach Planner.',
  },
};

export default function AdminRefereeCodes({ canEdit, kind = 'referee' }) {
  const cfg = KINDS[kind] || KINDS.referee;
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState({ code: '', label: '', displayOrder: '' });
  const [adding, setAdding] = useState(false);
  const [newDraft, setNewDraft] = useState({ code: '', label: '', displayOrder: '' });

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await auditFetch(`${API_BASE}/${cfg.path}`);
      const data = await res.json().catch(() => ([]));
      if (!res.ok) throw new Error(data?.error || `HTTP ${res.status}`);
      setCodes(Array.isArray(data) ? data : []);
    } catch (e) { setError(e.message || 'Failed to load codes'); }
    setLoading(false);
  }, [cfg.path]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (c) => {
    setEditingId(c.id);
    setDraft({ code: c.code, label: c.label || '', displayOrder: String(c.displayOrder) });
  };

  const cancelEdit = () => { setEditingId(null); setDraft({ code: '', label: '', displayOrder: '' }); };

  const saveEdit = async (id) => {
    setError('');
    try {
      const res = await auditFetch(`${API_BASE}/${cfg.path}/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: draft.code.trim(),
          label: draft.label.trim() || null,
          displayOrder: parseInt(draft.displayOrder, 10)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      cancelEdit();
      await load();
    } catch (e) { setError(e.message); }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archive code "${c.code}"? ${cfg.archiveNote}`)) return;
    setError('');
    try {
      const res = await auditFetch(`${API_BASE}/${cfg.path}/${c.id}`, { method: 'DELETE' });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || `HTTP ${res.status}`);
      }
      await load();
    } catch (e) { setError(e.message); }
  };

  const startAdd = () => {
    setAdding(true);
    const nextOrder = codes.reduce((m, c) => Math.max(m, c.displayOrder || 0), 0) + 1;
    setNewDraft({ code: '', label: '', displayOrder: String(nextOrder) });
  };

  const cancelAdd = () => { setAdding(false); setNewDraft({ code: '', label: '', displayOrder: '' }); };

  const saveAdd = async () => {
    setError('');
    try {
      const res = await auditFetch(`${API_BASE}/${cfg.path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: newDraft.code.trim(),
          label: newDraft.label.trim() || null,
          displayOrder: parseInt(newDraft.displayOrder, 10) || undefined
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      cancelAdd();
      await load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="admin-section">
      <h2>{cfg.title}</h2>
      <p style={{ color: '#555', maxWidth: 700, marginBottom: '1rem' }}>{cfg.intro}</p>

      {error && <div className="error-message" style={{ marginBottom: '0.75rem' }}>{error}</div>}
      {loading && <div className="loading">Loading...</div>}

      {!loading && (
        <>
          <table style={{ width: '100%', maxWidth: 760, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f5f5f5', textAlign: 'left' }}>
                <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd', width: 80 }}>Order</th>
                <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd', width: 100 }}>Code</th>
                <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd' }}>Label</th>
                {canEdit && <th style={{ padding: '0.5rem', borderBottom: '1px solid #ddd', width: 200 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {codes.length === 0 && (
                <tr><td colSpan={canEdit ? 4 : 3} style={{ padding: '0.75rem', color: '#888' }}>No {cfg.noun} codes configured.</td></tr>
              )}
              {codes.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #eee' }}>
                  {editingId === c.id ? (
                    <>
                      <td style={{ padding: '0.4rem' }}>
                        <input type="number" value={draft.displayOrder}
                          onChange={(e) => setDraft({ ...draft, displayOrder: e.target.value })}
                          style={{ width: 60 }} />
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        <input type="text" value={draft.code} maxLength={10}
                          onChange={(e) => setDraft({ ...draft, code: e.target.value })}
                          style={{ width: 80, fontWeight: 700 }} />
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        <input type="text" value={draft.label} maxLength={100}
                          onChange={(e) => setDraft({ ...draft, label: e.target.value })}
                          style={{ width: '95%' }} />
                      </td>
                      <td style={{ padding: '0.4rem' }}>
                        <button onClick={() => saveEdit(c.id)} style={{ marginRight: 6 }}>Save</button>
                        <button onClick={cancelEdit}>Cancel</button>
                      </td>
                    </>
                  ) : (
                    <>
                      <td style={{ padding: '0.5rem' }}>{c.displayOrder}</td>
                      <td style={{ padding: '0.5rem', fontWeight: 700 }}>
                        <span style={{
                          display: 'inline-block', padding: '2px 8px', background: '#eceff1',
                          border: '1px solid #b0bec5', borderRadius: 3, color: '#37474f'
                        }}>{c.code}</span>
                      </td>
                      <td style={{ padding: '0.5rem' }}>{c.label || <span style={{ color: '#888' }}>—</span>}</td>
                      {canEdit && (
                        <td style={{ padding: '0.5rem' }}>
                          <button onClick={() => startEdit(c)} style={{ marginRight: 6 }}>Edit</button>
                          <button onClick={() => archive(c)} style={{ color: '#c62828' }}>Archive</button>
                        </td>
                      )}
                    </>
                  )}
                </tr>
              ))}
              {adding && (
                <tr style={{ background: '#fffde7' }}>
                  <td style={{ padding: '0.4rem' }}>
                    <input type="number" value={newDraft.displayOrder}
                      onChange={(e) => setNewDraft({ ...newDraft, displayOrder: e.target.value })}
                      style={{ width: 60 }} />
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    <input type="text" value={newDraft.code} maxLength={10} placeholder="e.g. M"
                      onChange={(e) => setNewDraft({ ...newDraft, code: e.target.value })}
                      style={{ width: 80, fontWeight: 700 }} autoFocus />
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    <input type="text" value={newDraft.label} maxLength={100} placeholder="e.g. Mentor"
                      onChange={(e) => setNewDraft({ ...newDraft, label: e.target.value })}
                      style={{ width: '95%' }} />
                  </td>
                  <td style={{ padding: '0.4rem' }}>
                    <button onClick={saveAdd} disabled={!newDraft.code.trim()} style={{ marginRight: 6 }}>Save</button>
                    <button onClick={cancelAdd}>Cancel</button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          {canEdit && !adding && (
            <button onClick={startAdd} style={{ marginTop: '0.75rem' }}>+ Add code</button>
          )}
        </>
      )}
    </div>
  );
}
